// ═══════════════════════════════════════════════════════════════
// Google Disk (sekce 2.4d, Fáze 2).
//
// Rozsah: drive.file — aplikace vidí jen soubory, které si přes ni uživatel
// otevře Pickerem nebo které sama vytvoří. Nejužší možné oprávnění, bez
// Google security auditu (stačí „testing" OAuth consent + přidaní test users).
//
// Tři stavební kameny:
//   1) OAuth token přes Google Identity Services (GIS) — token client,
//      implicitní (access token v prohlížeči, bez refresh tokenu).
//   2) Google Picker — nativní dialog pro výběr .docx z Disku.
//   3) Drive REST v3 — stažení obsahu souboru a upload (multipart) zpět.
//
// Skripty GIS (accounts.google.com/gsi/client) a Picker (apis.google.com)
// se načítají dynamicky až při prvním použití, ne natvrdo v index.html.
//
// Konfigurace (Vite env, viz .env.example):
//   VITE_GOOGLE_CLIENT_ID  — OAuth 2.0 Client ID (Web application)
//   VITE_GOOGLE_API_KEY    — API key (pro Picker; omezený na doménu)
// ═══════════════════════════════════════════════════════════════

const CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID as string | undefined;
const API_KEY = import.meta.env.VITE_GOOGLE_API_KEY as string | undefined;
// App ID = číslo projektu (project number), tj. číslice na začátku Client ID.
// Je NUTNÉ pro Picker při scope drive.file — bez něj výběr souboru appce ten
// soubor neautorizuje a následné stažení vrátí 404. Když není v env, odvodíme
// ho z Client ID (část před první pomlčkou).
const APP_ID =
  (import.meta.env.VITE_GOOGLE_APP_ID as string | undefined) ||
  (CLIENT_ID ? CLIENT_ID.split('-')[0] : undefined);
const SCOPE = 'https://www.googleapis.com/auth/drive.file';
const DOCX_MIME = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

// ── Stav tokenu ──────────────────────────────────────────────
let accessToken: string | null = null;
let tokenExpiry = 0; // epoch ms
let tokenClient: any = null;

// ── Načtení externích skriptů (jen jednou) ───────────────────
const loaded: Record<string, Promise<void>> = {};
function loadScript(src: string): Promise<void> {
  const existing = loaded[src];
  if (existing) return existing;
  loaded[src] = new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = src;
    s.async = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error('Nepodařilo se načíst ' + src));
    document.head.appendChild(s);
  });
  return loaded[src];
}

/** Je Google Disk nakonfigurovaný (má client ID)? */
export function isGoogleConfigured(): boolean {
  return !!CLIENT_ID;
}

/** Zajistí platný access token (interaktivně vyžádá souhlas, když chybí). */
export async function ensureToken(interactive = true): Promise<string> {
  if (!CLIENT_ID) throw new Error('Google Disk není nakonfigurovaný (chybí VITE_GOOGLE_CLIENT_ID).');
  if (accessToken && Date.now() < tokenExpiry - 60_000) return accessToken;

  await loadScript('https://accounts.google.com/gsi/client');

  return new Promise((resolve, reject) => {
    const google = (window as any).google;
    if (!google?.accounts?.oauth2) {
      reject(new Error('Google Identity Services se nenačetly.'));
      return;
    }
    if (!tokenClient) {
      tokenClient = google.accounts.oauth2.initTokenClient({
        client_id: CLIENT_ID,
        scope: SCOPE,
        callback: (resp: any) => {
          if (resp.error) {
            reject(new Error('Přihlášení ke Google selhalo: ' + resp.error));
            return;
          }
          accessToken = resp.access_token;
          // expires_in je v sekundách; drž si vlastní expiry.
          tokenExpiry = Date.now() + (resp.expires_in ? resp.expires_in * 1000 : 3600_000);
          resolve(accessToken as string);
        },
      });
    }
    // prompt: '' = tiché obnovení, když už souhlas existuje; 'consent' vynutí dialog.
    tokenClient.requestAccessToken({ prompt: interactive ? '' : 'none' });
  });
}

// ── Picker ───────────────────────────────────────────────────
export interface DrivePick {
  id: string;
  name: string;
}

/** Otevře Google Picker pro výběr jednoho .docx a vrátí jeho id + název. */
export async function pickDriveDocx(): Promise<DrivePick | null> {
  const token = await ensureToken(true);
  await loadScript('https://apis.google.com/js/api.js');
  await new Promise<void>((resolve) => (window as any).gapi.load('picker', () => resolve()));

  const google = (window as any).google;
  return new Promise((resolve) => {
    const view = new google.picker.DocsView(google.picker.ViewId.DOCS)
      .setMimeTypes(DOCX_MIME)
      .setMode(google.picker.DocsViewMode.LIST);

    const builder = new google.picker.PickerBuilder()
      .addView(view)
      .setOAuthToken(token);
    // App ID propojí vybraný soubor s naší appkou → drive.file autorizace.
    // Bez něj Drive API vrátí na stažení 404.
    if (APP_ID) builder.setAppId(APP_ID);
    builder.setCallback((data: any) => {
      if (data.action === google.picker.Action.PICKED) {
        const doc = data.docs && data.docs[0];
        resolve(doc ? { id: doc.id, name: doc.name } : null);
      } else if (data.action === google.picker.Action.CANCEL) {
        resolve(null);
      }
    });
    if (API_KEY) builder.setDeveloperKey(API_KEY);
    builder.build().setVisible(true);
  });
}

// ── Drive REST ───────────────────────────────────────────────

/** Stáhne obsah souboru z Disku jako ArrayBuffer. */
export async function downloadDriveFile(fileId: string): Promise<ArrayBuffer> {
  const token = await ensureToken(true);
  const res = await fetch(
    `https://www.googleapis.com/drive/v3/files/${encodeURIComponent(fileId)}?alt=media`,
    { headers: { Authorization: 'Bearer ' + token } },
  );
  if (!res.ok) throw new Error('Stažení z Google Disku selhalo (' + res.status + ').');
  return await res.arrayBuffer();
}

/** Zjistí název souboru na Disku. */
export async function getDriveFileName(fileId: string): Promise<string> {
  const token = await ensureToken(true);
  const res = await fetch(
    `https://www.googleapis.com/drive/v3/files/${encodeURIComponent(fileId)}?fields=name`,
    { headers: { Authorization: 'Bearer ' + token } },
  );
  if (!res.ok) throw new Error('Načtení metadat z Google Disku selhalo.');
  const json = await res.json();
  return (json.name as string) || 'dokument.docx';
}

/**
 * Aktualizuje obsah existujícího souboru na Disku (PATCH media upload).
 * Používá se pro „Uložit" u dokumentu otevřeného z Disku.
 */
export async function updateDriveFile(fileId: string, blob: Blob): Promise<void> {
  const token = await ensureToken(true);
  const res = await fetch(
    `https://www.googleapis.com/upload/drive/v3/files/${encodeURIComponent(fileId)}?uploadType=media`,
    {
      method: 'PATCH',
      headers: { Authorization: 'Bearer ' + token, 'Content-Type': DOCX_MIME },
      body: blob,
    },
  );
  if (!res.ok) throw new Error('Uložení na Google Disk selhalo (' + res.status + ').');
}

/**
 * Vytvoří nový .docx na Disku (multipart: metadata + obsah) a vrátí jeho id.
 * Používá se pro „Uložit na Google Disk jako".
 */
export async function createDriveFile(name: string, blob: Blob): Promise<string> {
  const token = await ensureToken(true);
  const metadata = {
    name: name.endsWith('.docx') ? name : name + '.docx',
    mimeType: DOCX_MIME,
  };
  const form = new FormData();
  form.append('metadata', new Blob([JSON.stringify(metadata)], { type: 'application/json' }));
  form.append('file', blob);

  const res = await fetch(
    'https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart&fields=id',
    {
      method: 'POST',
      headers: { Authorization: 'Bearer ' + token },
      body: form,
    },
  );
  if (!res.ok) throw new Error('Vytvoření souboru na Google Disku selhalo (' + res.status + ').');
  const json = await res.json();
  return json.id as string;
}
